import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getHome, getLeadIntentCopy, getSite, isInfoPath } from '../content/index.js';
import { COMPACT_QUERY, useMediaQuery } from '../hooks/useMediaQuery.js';
import LeadForm from './LeadForm.jsx';
import { LeadModalContext } from './LeadModalContext.js';

/**
 * Auto-popup rules
 * - At most ONE automatic popup per full page load (a reload resets it).
 * - Never on info pages (privacy, terms, about, contact).
 * - Never after the user has focused/typed in any lead form, opened the modal
 *   manually, or submitted a lead (remembered for 30 days).
 * - Dismissing the popup does not schedule another one.
 */
const AUTO_OPEN_MS = 40000;
const RETRY_MS = 10000;
const AUTO_CLOSE_AFTER_SUCCESS_MS = 2500;
const SUBMITTED_KEY = 'bs_lead_submitted';
const SUBMITTED_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function wasRecentlySubmitted() {
  try {
    const raw = window.localStorage.getItem(SUBMITTED_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < SUBMITTED_TTL_MS;
  } catch {
    return false;
  }
}

function rememberSubmitted() {
  try {
    window.localStorage.setItem(SUBMITTED_KEY, String(Date.now()));
  } catch {
    // storage unavailable (private mode); in-memory flag still applies
  }
}

export { useLeadModal } from './LeadModalContext.js';

export default function LeadModalProvider({ children }) {
  const isCompact = useMediaQuery(COMPACT_QUERY);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState({});

  const autoTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const autoShownRef = useRef(false);
  const engagedRef = useRef(false);
  const submittedRef = useRef(wasRecentlySubmitted());
  const openRef = useRef(false);
  const pathRef = useRef(location.pathname);

  const site = getSite();
  const home = getHome();

  pathRef.current = location.pathname;
  openRef.current = open;

  const clearAutoTimer = useCallback(() => {
    if (autoTimerRef.current != null) {
      window.clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  /** Stop any future automatic popup for this page load. */
  const markEngaged = useCallback(() => {
    engagedRef.current = true;
    clearAutoTimer();
  }, [clearAutoTimer]);

  const openLeadModal = useCallback(
    (config = {}) => {
      markEngaged();
      clearCloseTimer();
      setModalConfig(config);
      setOpen(true);
    },
    [markEngaged, clearCloseTimer]
  );

  const closeLeadModal = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
    setModalConfig({});
  }, [clearCloseTimer]);

  const notifyLeadSubmitted = useCallback(
    ({ keepOpen = false } = {}) => {
      submittedRef.current = true;
      rememberSubmitted();
      markEngaged();
      if (openRef.current && !keepOpen) {
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => {
          closeTimerRef.current = null;
          setOpen(false);
          setModalConfig({});
        }, AUTO_CLOSE_AFTER_SUCCESS_MS);
      }
    },
    [markEngaged, clearCloseTimer]
  );

  // One automatic popup per page load. Mounted once at the app root, so SPA
  // navigation does not restart it; a reload does.
  useEffect(() => {
    if (submittedRef.current) {
      return undefined;
    }

    function fire() {
      autoTimerRef.current = null;
      if (autoShownRef.current || engagedRef.current || submittedRef.current) {
        return;
      }
      if (openRef.current) {
        return;
      }
      // Last line of defence: a form that is focused or partly filled means the
      // user is already engaged, whatever events we did or did not see.
      const active = document.activeElement;
      const filled = Array.from(
        document.querySelectorAll('.lead-card input[type="text"], .lead-card input[type="tel"], .lead-card input[type="email"], .lead-card input:not([type])')
      ).some((input) => input.value && input.value.trim() !== '');
      if ((active instanceof Element && active.closest('.lead-card')) || filled) {
        markEngaged();
        return;
      }
      if (isInfoPath(pathRef.current)) {
        autoTimerRef.current = window.setTimeout(fire, RETRY_MS);
        return;
      }
      autoShownRef.current = true;
      setModalConfig({});
      setOpen(true);
    }

    autoTimerRef.current = window.setTimeout(fire, AUTO_OPEN_MS);
    return () => {
      clearAutoTimer();
      clearCloseTimer();
    };
  }, [clearAutoTimer, clearCloseTimer]);

  // Close a manually opened modal when the user navigates elsewhere.
  useEffect(() => {
    setOpen(false);
    setModalConfig({});
  }, [location.pathname]);

  // Any interaction with an inline lead form means "already engaged".
  useEffect(() => {
    // focusin alone is not enough: it does not fire when the window is not
    // focused, and autofill / paste can change a field without a focus event.
    const EVENTS = ['focusin', 'pointerdown', 'keydown', 'input', 'change'];
    function onInteract(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest('.lead-card')) {
        markEngaged();
      }
    }

    EVENTS.forEach((name) => document.addEventListener(name, onInteract, true));
    return () => EVENTS.forEach((name) => document.removeEventListener(name, onInteract, true));
  }, [markEngaged]);

  // Intentional triggers only: "Talk to a counsellor" style links.
  useEffect(() => {
    function onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest('.lead-modal, .lead-modal-backdrop')) {
        return;
      }
      const counselLink = target.closest('a[href="#counsel"], a.nav-link.primary');
      if (counselLink) {
        event.preventDefault();
        openLeadModal({ intent: 'counsel' });
      }
    }

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [openLeadModal]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKey(event) {
      if (event.key === 'Escape') {
        closeLeadModal();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, closeLeadModal]);

  const intent = modalConfig.intent || 'counsel';
  const intentCopy = getLeadIntentCopy(intent);
  const isCounsel = intent === 'counsel';
  const isBrochure = intent === 'brochure';
  const brochureRequestOnly = isBrochure && modalConfig.brochureAvailable === false;
  const defaultLeadSource =
    location.pathname === '/'
      ? 'popup_home'
      : location.pathname.startsWith('/universities/')
        ? 'popup_university'
        : 'popup';
  const leadSource = modalConfig.leadSource || (isCounsel ? defaultLeadSource : `popup_${intent}`);

  let title = site.ctaPrimary;
  let subtitle = home.counselSubheading;
  let submitLabel;
  let successMessage;
  if (!isCounsel) {
    title = intentCopy.title;
    subtitle = intentCopy.subtitle;
    submitLabel = intentCopy.submit;
    if (brochureRequestOnly) {
      title = intentCopy.titleRequest || title;
      subtitle = intentCopy.subtitleRequest || subtitle;
      submitLabel = intentCopy.submitRequest || submitLabel;
      successMessage = intentCopy.successRequest;
    }
  }
  title = modalConfig.title || title;
  subtitle = modalConfig.subtitle || subtitle;
  submitLabel = modalConfig.submitLabel || submitLabel;

  function handleBrochureSuccess(payload) {
    if (!isBrochure || !payload?.brochureToken) {
      return;
    }
    const link = document.createElement('a');
    link.href = `/api/brochures/${payload.brochureToken}`;
    link.setAttribute('download', '');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  return (
    <LeadModalContext.Provider
      value={{ openLeadModal, closeLeadModal, notifyLeadSubmitted, markEngaged, isCompact }}
    >
      {children}
      {open && (
        <div className="lead-modal-backdrop" onClick={closeLeadModal} role="presentation">
          <div
            className="lead-modal"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(event) => event.stopPropagation()}
          >
            <LeadForm
              key={`${intent}-${leadSource}-${modalConfig.preferredProgram || ''}`}
              title={title}
              subtitle={subtitle}
              leadSource={leadSource}
              submitLabel={submitLabel}
              intent={intent}
              defaultProgram={modalConfig.preferredProgram || ''}
              extraPayload={modalConfig.extraPayload}
              successMessage={successMessage}
              keepOpenAfterSuccess={isBrochure && !brochureRequestOnly}
              onSuccess={handleBrochureSuccess}
              onClose={closeLeadModal}
            />
          </div>
        </div>
      )}
    </LeadModalContext.Provider>
  );
}
